/**
 * Changing an agreed price, and saying why (spec §1.2).
 *
 * The backend writes a history row — field, old value, new value, who, when,
 * reason — on every locked-price change. The reason is the only one of those
 * it cannot work out for itself, so it has to come from here.
 *
 * A reason is asked for only when a *locked* price actually moves. An
 * unlocked line is still being negotiated and its figures are expected to
 * change; demanding a note for each keystroke would teach people to type "x".
 *
 * Pure. The component holds the form and passes it in.
 */

const isBlank = (value) =>
  value === null || value === undefined || String(value).trim() === '';

/** A finite number, or null when the value is not one. Blank counts as zero. */
function money(value) {
  if (isBlank(value)) return 0;
  const parsed = typeof value === 'number' ? value : parseFloat(String(value).trim());
  return Number.isFinite(parsed) ? parsed : null;
}

const text = (value) => (isBlank(value) ? '' : String(value).trim());

/** The column the reason lands in. Checked here so a save cannot fail on it. */
export const REASON_MAX = 255;

/**
 * Whether either figure differs from what is on the row.
 *
 * Compared as money rather than as text, because the server returns
 * "40000.00" and the input holds "40000" — the same figure, and a save that
 * asked for a reason to explain no change would be nonsense.
 */
export function priceMoved(row, next) {
  const record = row || {};
  const form = next || {};

  return (
    money(form.payout) !== money(record.influencer_final_price) ||
    money(form.billing) !== money(record.final_price)
  );
}

/** Whether this save has to carry a reason. */
export function needsReason(row, next) {
  return Boolean((row || {}).price_locked) && priceMoved(row, next);
}

/** `{field: message}`; empty means valid. */
export function validatePriceChange(row, next, reason) {
  if (!needsReason(row, next)) return {};

  const note = text(reason);
  if (!note) {
    return { reason: 'Say why the agreed price is changing. It goes on the line’s history.' };
  }
  if (note.length > REASON_MAX) {
    return { reason: `Keep the reason to ${REASON_MAX} characters. That one is ${note.length}.` };
  }
  return {};
}

/**
 * The request body. Both figures go every time: the endpoint takes them
 * together, so sending one clears the other.
 */
export function pricePayload(row, next, { reason, extra } = {}) {
  const form = next || {};
  const note = needsReason(row, next) ? text(reason) : '';

  return {
    influencer_final_price: form.payout,
    final_price: form.billing,
    ...(extra || {}),
    ...(note ? { reason: note } : {}),
  };
}
